import type { GroupKey, SectionKey } from './groups';

/**
 * "Is this apartment set up?", answered from what the SERVER holds — never from what the form
 * currently shows, and never stored. An unsaved edit is not really set (the host can still discard
 * it, and neither the guest nor Ubyport can see it), and a stored "done" flag would be wrong after a
 * reload, on another device, or after something is deleted. Same rule as `utils/setupStep.ts`.
 *
 * <p>Pure on purpose: the page fetches, this decides.
 */

export type ChecklistItemId =
  | 'calendar' | 'ubyportLogin' | 'ubyportAuto'
  | 'keyAccess' | 'wifi' | 'guideEnabled' | 'stripe';

/** `loading`: the answer is not known yet (never render a count from it). `na`: nothing to do. */
export type ChecklistStatus = 'done' | 'todo' | 'na' | 'loading';

export interface JumpTarget {
  group: GroupKey;
  section: SectionKey;
  /** Id of the field to land on. The page falls back to the section if it is not on screen. */
  targetId?: string;
  /** Always-rendered neighbour to use when `targetId` is behind a condition (e.g. the door code). */
  fallbackId?: string;
}

export interface ChecklistItem {
  id: ChecklistItemId;
  status: ChecklistStatus;
  jump: JumpTarget;
  /** The group tab that gets a warning badge while this item is `todo`. */
  group: GroupKey;
}

export interface SavedApartment {
  calendarSyncOk?: boolean | null;
  ubyportUsername?: string | null;
  hasUbyportPassword?: boolean | null;
  idub?: string | null;
  autoSendToUbyport?: boolean | null;
  cityTaxCollectionEnabled?: boolean | null;
  paymentMethodId?: number | null;
}

export interface SavedGuide {
  enabled?: boolean | null;
  keyAccessType?: string | null;
  wifiName?: string | null;
}

export interface StripeAccountLike {
  id: number;
  type: string;
  stripeChargesEnabled?: boolean | null;
}

/**
 * `undefined` = still loading, `null` = the request failed (or the answer cannot be trusted).
 * The two differ for settings that only decide whether Stripe is needed: a failed read must not
 * block the host, a pending one must not produce a count.
 */
export interface ChecklistInput {
  /** The apartment as last loaded from the server. `null` until the first load. */
  saved: SavedApartment | null;
  guide: SavedGuide | null | undefined;
  deposit: { enabled: boolean } | null | undefined;
  extensions: { mode: string; luggageMode: string } | null | undefined;
  paymentMethods: StripeAccountLike[];
}

const filled = (v: string | null | undefined) => !!v && v.trim().length > 0;
const asStatus = (ok: boolean): ChecklistStatus => (ok ? 'done' : 'todo');

/**
 * Item 7. One function so that per-purpose Stripe accounts (Issue #119) can replace "the apartment's
 * single account" here and nowhere else.
 */
export function stripeAccountStatus(input: ChecklistInput): ChecklistStatus {
  const { saved, deposit, extensions, paymentMethods } = input;
  if (!saved) return 'loading';

  const cityTax = !!saved.cityTaxCollectionEnabled;
  const depositOn = deposit ? deposit.enabled : false;
  const extensionsOn = extensions ? extensions.mode !== 'OFF' || extensions.luggageMode !== 'OFF' : false;

  // A charge is already known to be needed: no point waiting for the rest.
  const needed = cityTax || depositOn || extensionsOn;
  if (!needed) {
    if (deposit === undefined || extensions === undefined) return 'loading';
    return 'na';
  }

  const account = paymentMethods.find(pm => pm.type === 'STRIPE' && pm.id === saved.paymentMethodId);
  return asStatus(!!account?.stripeChargesEnabled);
}

export function deriveChecklist(input: ChecklistInput): ChecklistItem[] {
  const { saved, guide } = input;
  const guideKnown = !!guide;
  const g = guide || {};

  const fromApartment = (f: (a: SavedApartment) => boolean): ChecklistStatus => (saved ? asStatus(f(saved)) : 'loading');
  const fromGuide = (f: (g: SavedGuide) => boolean): ChecklistStatus => (guideKnown ? asStatus(f(g)) : 'loading');

  return [
    {
      id: 'calendar', group: 'reservations',
      status: fromApartment(a => a.calendarSyncOk != null),
      jump: { group: 'reservations', section: 'calendar' },
    },
    {
      id: 'ubyportLogin', group: 'authorities',
      status: fromApartment(a => filled(a.ubyportUsername) && !!a.hasUbyportPassword && filled(a.idub)),
      jump: { group: 'authorities', section: 'ubyport', targetId: 'ae-ubyportUsername' },
    },
    {
      id: 'ubyportAuto', group: 'authorities',
      status: fromApartment(a => !!a.autoSendToUbyport),
      jump: { group: 'authorities', section: 'ubyport', targetId: 'ae-autoSendToUbyport' },
    },
    {
      id: 'keyAccess', group: 'arrival',
      status: fromGuide(x => !!x.keyAccessType),
      jump: { group: 'arrival', section: 'guide', targetId: 'ae-guide-keyAccessType' },
    },
    {
      id: 'wifi', group: 'arrival',
      status: fromGuide(x => filled(x.wifiName)),
      jump: { group: 'arrival', section: 'guide', targetId: 'ae-guide-wifiName' },
    },
    {
      id: 'guideEnabled', group: 'arrival',
      status: fromGuide(x => !!x.enabled),
      jump: { group: 'arrival', section: 'guide', targetId: 'guideEnabled' },
    },
    {
      id: 'stripe', group: 'money',
      status: stripeAccountStatus(input),
      jump: { group: 'money', section: 'payments' },
    },
  ];
}

export interface ChecklistSummary {
  /** True while any answer is unknown: show a neutral state instead of a count. */
  loading: boolean;
  done: number;
  total: number;
  allDone: boolean;
}

/** `na` counts as done, so a host who charges nothing can still reach 7 of 7. */
export function summarizeChecklist(items: ChecklistItem[]): ChecklistSummary {
  const loading = items.some(i => i.status === 'loading');
  const done = items.filter(i => i.status === 'done' || i.status === 'na').length;
  return { loading, done, total: items.length, allDone: !loading && done === items.length };
}

/** Groups that should wear the amber badge: any of their items still `todo`. */
export function groupsNeedingAttention(items: ChecklistItem[]): Set<GroupKey> {
  return new Set(items.filter(i => i.status === 'todo').map(i => i.group));
}

/** Per-viewer UI preference only — completeness itself is always derived. */
export const CHECKLIST_OPEN_KEY = 'apartmentEdit.checklistOpen';

/**
 * Open the first time, on every screen size. Once the host toggles it, the browser remembers that
 * choice (CHECKLIST_OPEN_KEY) and it is no longer a default. A finished checklist is never open.
 */
export function defaultChecklistOpen(): boolean {
  return true;
}
